import api from './api';

/**
 * Accounting Dashboards — the bucket cache read side.
 * Backed by app/api/api_v1/endpoints/accounting_dashboards.py (both GET, cache-only).
 * The cache is rebuilt every 5 minutes by the Sync job `accounting_dashboard_cache`;
 * `syncedAt` is that rebuild's stamp and is what the page shows as "Last Updated".
 */

export type AcctMemberType = 'pro' | 'invoice' | 'addlcost' | 'template';

export interface AcctBucketSummary {
  count: number;
  /** Net invoice dollars across the bucket's members (0 for payables/templates). */
  total: number;
}

export interface AcctSummaryResponse {
  /** ISO datetime of the cache rebuild, or null when the cache has never been built. */
  syncedAt: string | null;
  /** Keyed by tile row key (accountingPanels.ts). Rows the cache does not produce are absent. */
  buckets: Record<string, AcctBucketSummary>;
}

/** Drill rows are shaped per member type; columns live in pages/accounting/drillColumns.tsx. */
export type AcctDrillRow = Record<string, unknown>;

/**
 * How the drill is DRAWN, which is not always what its members are. V1 stores one
 * representative pro per masterpro in the Pending Invoicing and Invoicing Hold buckets and
 * renders those screens one-row-per-masterpro; every other 'pro' bucket stays per-pro.
 * See accounting_dashboards.py::row_type_for.
 */
export type AcctRowType = AcctMemberType | 'masterpro' | 'masterpro_hold';

export interface AcctDrillResponse {
  memberType: AcctMemberType;
  rowType: AcctRowType;
  /** Whether this bucket's rows can be narrowed by an orders filter. False for template
   *  buckets, whose members have no order at all — the grid hides the control there. */
  filterable?: boolean;
  rows: AcctDrillRow[];
}

export const ACCT_SUMMARY_PATH = '/accounting-dashboards/summary';
export const acctDrillPath = (bucketKey: string): string =>
  `/accounting-dashboards/drill/${encodeURIComponent(bucketKey)}`;

export const getAcctSummary = async (): Promise<AcctSummaryResponse> =>
  (await api.get<AcctSummaryResponse>(ACCT_SUMMARY_PATH)).data;

export const getAcctDrill = async (bucketKey: string): Promise<AcctDrillResponse> =>
  (await api.get<AcctDrillResponse>(acctDrillPath(bucketKey))).data;
